import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, NgModule, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { HookResult, StateService, Transition, UIRouterModule } from '@uirouter/angular';

import { TranslateService } from '@codeandweb/ngx-translate';

import { Observable } from 'rxjs';

import { CalendarModule } from '@app/primeng-overrides/calendar';
import { ConfirmationService } from 'primeng/api';
import { DataView } from 'primeng/dataview';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { VirtualScrollerModule } from 'primeng/virtualscroller';


import { EventManagerService } from '@app/_global/event-manager.service';
import { AbonneInfosTabModule } from '@app/abonne/abonne-infos-tab/abonne-infos-tab.component';
import { HTMLEditorModule } from '@app/components/html-editor';
import { Rubrique } from '@app/nomenclature/nomenclature.model';
import { NomenclaturePipe } from '@app/nomenclature/nomenclature.pipe';
import { NomenclatureService } from '@app/nomenclature/nomenclature.service';
import { Utilisateur } from '@app/utilisateur/utilisateur.model';
import { GlobalModule } from '@global/global.module';
import { AjoutRubriqueDialogComponent } from './ajout-rubrique-dialog/ajout-rubrique-dialog';
import { DeplacementArticlesDialogComponent } from './deplacement-articles-dialog';
import { EditionArticleDialogComponent } from './edition-article-dialog';
import { FutureVersionArticleComponent } from './future-version-article-dialog';
import { HistoriqueArticleDialogComponent } from './historique-article-dialog';
import { HistoriqueTexteDialogComponent } from './historique-texte-dialog';
import { LinkedTextesDialogComponent } from './linked-textes-dialog';
import { OperationsSectionModule } from './operations-section/operations-section';
import { StatutRecolementSelectorModule } from './statut-recolement-selector';
import { Article, InfoTexteModification, ModificationsArticle, OperationModificative, Texte } from './texte.model';
import { ValidationModificationsDialogComponent } from './validation-modifications-dialog';

import { ExtensibleObject, normalizeStringForSearch, simpleComparison, uid } from '@app/_helpers/utils';
import { environment } from '@environments/environment';

@Component({
	selector: 'texte',
	templateUrl: './texte.html'
})
export class TexteComponent implements OnInit, AfterViewInit {

	private _uuid: string = uid();
	get uuid(): string { return this._uuid; }

	@ViewChild('dataViewArticles', { static: true }) dataViewArticles: DataView;
	@ViewChild('rubriques', { static: true, read: ElementRef }) rubriquesDiv: ElementRef<HTMLDivElement>;
	rubriquesExpanded?: boolean = undefined; // undefined si l'expansion/réduction n'est pas disponible

	@Input() me: Utilisateur;
	@Input() texte: Texte;

	pristineTexte: Texte;
	hasPristineTexteModificativeArticles: boolean = false;

	isCreation = false;
	editMode = false;
	canEdit = false;
	submitted = false;
	skipExitStateCheck = false;
	today = new Date();

	articles: Article[] = [];
	modifsArticles: Record<number/*art_id*/, ModificationsArticle> = {};
	expandedArticles: Record<number, boolean> = {};
	search: string | null;
	initialRowsPerPage: number = environment.default_rows;
	currentRowsPerPage: number = this.initialRowsPerPage;

	filteredArticlesCache: {search?: string, articles: Article[]} = {search: '', articles: []}

	showReferenceDuplicationWarning = false;

	constructor(
		private translateService: TranslateService,
		private dialogService: DialogService,
		private confirmationService: ConfirmationService,
		private eventManager: EventManagerService,
		private stateService: StateService,
		private nomenclatureService:  NomenclatureService,
		private nomenclaturePipe: NomenclaturePipe
	) {
	}

	ngOnInit(): void {
		this.canEdit = this.me.uti_administrateur;
		this.isCreation = this.texte == undefined;
		if (this.isCreation) {
			this.texte = {
				txt_modification_majeure: false,
				txt_brouillon: true,
				txt_abrogation: false,
				txt_actif: true,
				txt_exclusivement_modificatif: false,
				operations_modificatives: [] as OperationModificative[],
				textes_modifies: [] as InfoTexteModification[],
				textes_modificatifs: [] as InfoTexteModification[],
				rubriques: [] as Rubrique[],
				articles: [] as Article[],
			} as Texte;
			this.editMode = true;
		}
		else {
			this.nomenclatureService.getModificationsArticles(this.texte.txt_id)
			.subscribe((modifs: ModificationsArticle[]) => {
				for (let modif of modifs) {
					this.modifsArticles[modif.art_id] = modif;
				}
			});

			this.texte.operations_modificatives ??= [];

			// On agrandit tous les articles courts par défaut et on arrange les art_ordre
			this.texte.articles.forEach((article: Article, index: number) => {
				if (article.art_corps && article.art_corps.length < 100) {
					this.expandedArticles[article.art_id] = true;
				}
				article.art_ordre = index;

				//@ts-ignore
				article.pristine = structuredClone(article);
			});

			// On agrandit forcément le premier article si il est seul
			if (this.texte.articles.length == 1) {
				this.expandedArticles[this.texte.articles[0].art_id] = true;
			}

			// On trie les rubriques par ordre alphabétique sur leur nom entier
			this.texte.rubriques = this.texte.rubriques.sort((rubA: Rubrique, rubB: Rubrique) =>  {
				let nomenclatureA = this.nomenclaturePipe.transform(rubA);
				let nomenclatureB = this.nomenclaturePipe.transform(rubB);
				return nomenclatureA < nomenclatureB ? - 1 : 1;
			});

			this.texte = structuredClone(this.texte);
			this.articles = [...this.texte.articles]
		}
		this.pristineTexte = structuredClone(this.texte);
		this.hasPristineTexteModificativeArticles = this.pristineTexte.articles.find((article: Article) => article.art_modificatif) != undefined;
	}

	ngAfterViewInit(): void {
		if (this.isCreation || this.rubriquesDiv.nativeElement.clientHeight < 120) {
			this.rubriquesDiv.nativeElement.classList.add('expanded');
		}
		else {
			this.rubriquesExpanded = false;
		}
	}

	toggleEditMode(status?: boolean) {
		this.editMode = (status !== undefined)? status : !!!this.editMode;
		if (this.editMode) {
			this.texte.txt_modification_majeure = false;
			this.texte.articles.forEach((article: Article) => article.art_modification_majeure = false);
			this.search = '';
			this.onArticleSearchChange();
		}
	}

	onReferenceChange() {
		if (this.texte.txt_reference == '') {
			this.showReferenceDuplicationWarning = false;
			return;
		}

		this.nomenclatureService.getTextes({txt_reference: this.texte.txt_reference})
		.subscribe(({ total }) => {
			let otherTextsCount  = total;
			if (!this.isCreation) {
				otherTextsCount--;
			}

			this.showReferenceDuplicationWarning = otherTextsCount > 0;
		});
	}

	onDateAbrogationChange() {
		let date_abrogation = this.texte.txt_date_abrogation;
		if (date_abrogation) {
			if (date_abrogation <= this.today) {
				this.texte.txt_abrogation = true;
			}
			else {
				this.texte.txt_abrogation = false;
			}
		}
	}

	onModifExclusivenessChange() {
		if (this.texte.txt_exclusivement_modificatif) {
			this.texte.rubriques = [];
		}
	}

	onDateAbrogationArticleChange(article: Article) {
		let date_abrogation = article.art_date_abrogation;
		if (date_abrogation) {
			if (date_abrogation <= this.today) {
				article.art_abrogation = true;
			}
			else {
				article.art_abrogation = false;
			}
		}
	}

	onArticleSearchChange() {
		this.articles = [...this.texte.articles];
		let search = this.search;
		if (search && search?.trim() != '') {
			search = search.trim();
				const regex = new RegExp(normalizeStringForSearch(search)!, 'i');

				this.articles = this.articles.filter((article: Article) => {
				let normalizedTitle = normalizeStringForSearch(article.art_titre ?? '');
				let normalizedBodyPlaintext = normalizeStringForSearch(article.art_corps_plaintext ?? '');

				return normalizedTitle.match(regex) || normalizedBodyPlaintext.match(regex);
			});
		}
	}

	expandRubriques(event: MouseEvent) {
		if (this.rubriquesExpanded === undefined || this.rubriquesExpanded) {
			return;
		}
		this.rubriquesExpanded = true;
		this.rubriquesDiv.nativeElement.classList.add('expanded');

		event.stopImmediatePropagation();
	}

	collapseRubriques(event: MouseEvent) {
		if (this.rubriquesExpanded === undefined || !this.rubriquesExpanded) {
			return;
		}
		this.rubriquesExpanded = false;
		this.rubriquesDiv.nativeElement.classList.remove('expanded');

		event.stopImmediatePropagation();
	}

	showRubriqueAdditionDialog() {
		const ref = this.dialogService.open(AjoutRubriqueDialogComponent, {
			data: {
				texte: this.pristineTexte
			},
			appendTo: 'body',
			header: this.translateService.instant('textes.link_to_rubrique_dialog_header'),
			width: '70rem',
		});

		ref.onClose.subscribe((rubrique?: Rubrique) => {
			if (rubrique != undefined && !this.texte.rubriques.some((rub: Rubrique) => rub.rub_id == rubrique.rub_id)) {
				this.texte.rubriques.push(rubrique);
			}
		});
	}

	removeRubrique(rubrique: Rubrique){
		this.confirmationService.confirm({
			defaultFocus: 'reject',
			message: this.translateService.instant('textes.unlink_rubrique_confirmation', {rubrique: '<b>'+rubrique.rub_nom+'</b>'}),
			accept: () => {
				this.texte.rubriques = this.texte.rubriques.filter((rub: Rubrique) => rub.rub_id != rubrique.rub_id);
			}
		});
	}

	async editArticle(article: Article) {
		const ref = this.dialogService.open(EditionArticleDialogComponent, {
			header: this.translateService.instant('textes.article_edition_dialog_header'),
			data: {
				article: structuredClone(article),
			},
			width: '80%'
		});
		ref.onClose.subscribe((editedArticle?: Article) => {
			if (editedArticle) {
				article.art_corps = editedArticle?.art_corps
			}
		});
	}

	async addArticle(index: number) {
		let article = {
			art_ordre: index,
			art_id: -Math.random(), // id temporaire utilisé seulement côté client
			art_separateur: false,
			art_modification_majeure: false,
			art_abrogation: false,
			art_date_abrogation: null,
			art_modificatif: this.texte.txt_exclusivement_modificatif
		} as Article;

		//@ts-ignore
		article.pristine = structuredClone(article);

		// Incrémentation de l'art_ordre des articles qui suivent le nouvel article
		let after = this.texte.articles.slice(index);
		after.forEach((article: Article) => article.art_ordre ++);

		this.texte.articles = [
			...this.texte.articles.slice(0, index), // before
			article,
			...after
		];
		this.articles = [...this.texte.articles];
		this.expandedArticles[article.art_id] = true;

		// Si l'article est ajouté à la fin et qu'il vient de causer la création d'une nouvelle page, on change de page
		if (after.length == 0 && (index % this.currentRowsPerPage) == 0) {
			this.dataViewArticles.first = index;
		}
	}

	openArticleMoveDialog(article: Article) {
		const ref = this.dialogService.open(DeplacementArticlesDialogComponent, {
			header: this.translateService.instant('textes.articles_move_dialog_header'),
			styleClass: 'min-w-30rem max-w-40rem',
			data: {
				articles: structuredClone(this.texte.articles),
				focusedArticleId: article.art_id
			}
		});
		ref.onClose.subscribe((articles?: Article[]) => {
			if (articles) {
				this.texte.articles = articles;
				this.articles = [...this.texte.articles];
			}
		});
	}

	deleteArticle(article: Article) {
		this.confirmationService.confirm({
			defaultFocus: 'reject',
			message: this.translateService.instant('textes.article_deletion_confirmation'),
			accept: async () => {
				let index = this.texte.articles.findIndex((art: Article) => art == article);
				// On enlève l'article
				this.texte.articles.splice(index, 1);
				this.articles = [...this.texte.articles];
				// On décrémente art_ordre des articles situés après celui qui vient d'être supprimé
				this.texte.articles.slice(index).forEach((article: Article) => article.art_ordre--);
			}
		});
	}

	submit({publish}: {publish?: boolean} = {}) {
		this.submitted = true;
		this.skipExitStateCheck = true;

		// Nettoyage des données temporaires des articles
		let texte = structuredClone(this.texte);
		texte.articles.forEach((art: ExtensibleObject) => {
			if (art.art_id < 0) {
				delete art.art_id;
			}
			delete art.pristine;
		});
		// Suppression du cache de texte cible
		this.deleteTargetTextCache(texte);

		if (this.isCreation) {
			this.nomenclatureService.createTexte(texte)
			.subscribe({
				next: ({ txt_id }) => {
					this.eventManager.emit('toast', { severity: 'success', summary: this.translateService.instant('textes.texte_created_success') });

					if (publish) {
						this.nomenclatureService.publishTexte(txt_id)
						.subscribe({
							complete: () => {
								this.eventManager.emit('toast', { severity: 'success', summary: this.translateService.instant('textes.texte_published_success') });
								this.stateService.go('^.single', {txt_id: txt_id});
							},
							error: () => {
								// En cas d'erreur de publication on se rend quand même sr le texte
								this.stateService.go('^.single', {txt_id: txt_id});
							}
						});
						return;
					}

					this.stateService.go('^.single', {txt_id: txt_id});
				}
			});
		}
		else {
			const saveText = (textToSave: Texte) => {
				this.nomenclatureService.updateTexte(textToSave)
				.subscribe(() => {
					this.eventManager.emit('toast', { severity: 'success', summary: this.translateService.instant('textes.texte_updated_success') });
					this.stateService.go('.', undefined, {reload: '^'});
				});
			};

			if (texte.txt_brouillon) {
				saveText(texte)
			}
			else {
				const ref = this.dialogService.open(ValidationModificationsDialogComponent, {
					header: this.translateService.instant('common.save_title'),
					styleClass: 'min-w-30rem max-w-40rem',
					data: {
						texte: structuredClone(texte),
					}
				});
				ref.onClose.subscribe((textToSave?: Texte) => {
					if (textToSave != undefined) {
						saveText(textToSave)
					}
				});
			}
		}

	}

	publish() {
		this.nomenclatureService.publishTexte(this.texte.txt_id)
		.subscribe({
			complete: () => {
				this.eventManager.emit('toast', { severity: 'success', summary: this.translateService.instant('textes.texte_published_success') });
				this.stateService.go('.', undefined, {reload: '^'});
			}
		})
	}

	uiCanExit(newTransition?: Transition): HookResult {
		let pristineTexte = structuredClone(this.pristineTexte);
		this.deleteTargetTextCache(pristineTexte);

		let texte = structuredClone(this.texte);
		this.deleteTargetTextCache(texte);
		
		if (!this.skipExitStateCheck && !simpleComparison(pristineTexte, texte)) {
			return new Observable<any>(sub => {
				this.confirmationService.confirm({
					defaultFocus: 'reject',
					header: this.translateService.instant('common.leaving_unsaved_form_confirm_header'),
					message: this.translateService.instant('common.leaving_unsaved_form_confirm_message'),
					accept: () => {
						sub.next(true);
						sub.complete();
					},
					reject: () => {
						sub.next(false);
						sub.complete();
					}
				});
			}).toPromise();
		}
	}


	showHistoryDialog() {
		this.dialogService.open(HistoriqueTexteDialogComponent, {
			header: this.translateService.instant('textes.history_modal_title', {text: this.texte.txt_reference}),
			width: '80rem',
			data: {
				txt_id: this.texte.txt_id
			}
		});
	}

	showTargetedTextesList() {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translateService.instant('textes.targeted_textes_modal_title'),
			width: '80%',
			data: {
				liste: this.texte.textes_modifies,
				mode: 'targeted',
				me: this.me
			}
		});
	}

	showModifyingTextesList() {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translateService.instant('textes.modifying_textes_button_label'),
			width: '80%',
			data: {
				liste: this.texte.textes_modificatifs,
				mode: 'modifying',
				me: this.me
			}
		});
	}

	showArticleHistoryDialog(article: Article) {
		this.dialogService.open(HistoriqueArticleDialogComponent, {
			header: this.translateService.instant('textes.article_history_modal_title', {article: article.art_titre}),
			width: '80rem',
			data: {
				art_id: article.art_id
			}
		});
	}

	showArticleModifyingTextesList(article: Article, modifs: ModificationsArticle) {
		this.dialogService.open(LinkedTextesDialogComponent, {
			header: this.translateService.instant('textes.modifying_textes_for_article', {article: article.art_titre}),
			width: '80%',
			data: {
				liste: modifs.textes_modificatifs,
				mode: 'modifying',
				me: this.me
			},
		});
	}

	showArticleFutureVersions(article: Article, modifs: ModificationsArticle) {
		this.dialogService.open(FutureVersionArticleComponent, {
			header: this.translateService.instant('textes.future_versions_of_article', {article: article.art_titre}),
			width: '80%',
			data: {
				article: article,
				future_versions: modifs.versions_article
			}
		});
	}


	deleteTargetTextCache(texte: Texte) {
		texte.operations_modificatives?.forEach((op: OperationModificative) => {
			delete op.txt_cible;
		});
	}

}

@NgModule({
	imports: [
		CommonModule,
		GlobalModule,
		UIRouterModule,
		FormsModule,
		ToolbarModule,
		PanelModule,
		DividerModule,
		AbonneInfosTabModule,
		CalendarModule,
		HTMLEditorModule,
		StatutRecolementSelectorModule,
		VirtualScrollerModule,
		TableModule,
		OperationsSectionModule,
	],
	exports: [TexteComponent],
	declarations: [TexteComponent]
})
export class TexteModule { }


